import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  fetchBookSummaries,
  fetchUser,
  fetchUserRoles,
  fetchPromotions,
} from "@/data/mockData";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Mail,
  Shield,
  Key,
  AlertTriangle,
  AlertCircle,
  Loader2,
  CreditCard,
  Fingerprint,
  RefreshCw,
  Phone,
  Calendar,
  Clock,
  LogIn,
  CheckCircle,
  User,
  Hash,
  Users,
  Settings,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, addDays, addMonths, addYears } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  banUser,
  unbanUser,
  deleteUser,
  resetUserPassword,
  resendConfirmationEmail,
  resendInvitation,
  confirmUserEmail,
  fetchUserAuthDetails,
  fetchUserReadingStats,
  fetchUserCategories,
  fetchUserCategoriesWithIds,
  addUserCategories,
  removeUserCategory,
  fetchAllCategories,
  fetchUserDeletionFeedback,
  fetchUserDevices,
  AccountDeletionFeedback,
} from "@/api/users";
import { getUserPushTokens } from "@/api/notifications";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchSubscriptionPlans } from "@/api/subscription-plans";
import { fetchCurrencies, fetchPaymentMethods } from "@/api/payments";
import {
  fetchUserSubscription,
  fetchUserLatestSubscription,
  fetchUserSubscriptionHistory,
  createUserSubscription,
  updateUserSubscription,
} from "@/api/user-subscriptions";
import { formatISO } from "date-fns";
// Import our new components
import { UserSubscriptionForm } from "@/components/user";
import { SubscriptionFormData } from "@/components/user/UserSubscriptionForm";
// Add imports for our new dialog components
import CancelSubscriptionDialog from "@/components/user/CancelSubscriptionDialog";
import ExtendSubscriptionDialog from "@/components/user/ExtendSubscriptionDialog";
import ChangeSubscriptionStatusDialog from "@/components/user/ChangeSubscriptionStatusDialog";
// Import our new UpgradeSubscriptionDialog component
import UpgradeSubscriptionDialog from "@/components/user/UpgradeSubscriptionDialog";
// Import user components
import {
  ReferralInfo,
  UserProfileHeader,
  UserStatistics,
  UserActions,
  CollaboratorSummary,
  UserTopicSubscriptions,
  UserNotifications,
} from "@/components/users";
// Import UserRecommendations component
import { UserRecommendations } from "@/components/user";
// Import UserActivityFeed component
import UserActivityFeed from "@/components/users/UserActivityFeed";

// Add imports for collaborator API functions
import { CollaboratorPromotion } from "@/types/collaborator";

// Import beta testing functions
import { isUserBetaTester, makeUserBetaTester } from "@/api/beta-testing";

// Interface for auth user information
interface AuthUserInfo {
  confirmed_at?: string;
  email_confirmed_at?: string;
  created_at: string;
  last_sign_in_at?: string;
  phone?: string;
  phone_confirmed_at?: string;
  is_anonymous?: boolean;
  app_metadata: Record<string, unknown> & {
    provider?: string;
    providers?: string[];
  };
  user_metadata: Record<string, unknown>;
  aud?: string;
  role?: string;
  updated_at?: string;
  identities?: {
    provider: string;
    id: string;
    created_at: string;
    updated_at: string;
  }[];
}

// Interfaces for subscription update data
interface SubscriptionCancelUpdateData {
  status: "active" | "cancelled" | "expired" | "pending";
  cancellationReason: string;
  cancellationDate: string;
  subscriptionEnd?: string;
  refunded?: boolean;
  refundDate?: string;
  refundAmount?: number;
  adminNotes?: string;
}

interface SubscriptionExtendUpdateData {
  subscriptionEnd: string;
  adminNotes?: string;
}

interface SubscriptionStatusUpdateData {
  status: "active" | "cancelled" | "expired" | "pending";
  adminNotes?: string;
}

// Helper functions still used in UserDetails
const getRoleBadgeColor = (roleId: number): string => {
  // Special colors for specific roles
  if (roleId === 1) {
    // SuperAdmin - Red
    return "bg-red-500/10 text-red-500";
  }
  if (roleId === 5) {
    // Standard User - Blue
    return "bg-blue-500/10 text-blue-500";
  }

  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];

  return colorPairs[roleId % colorPairs.length];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "bg-green-500";
    case "cancelled":
      return "bg-yellow-500";
    case "expired":
      return "bg-gray-500";
    default:
      return "bg-blue-500";
  }
};

const formatStatusText = (status: string) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const calculateProgress = (start: string, end: string) => {
  const now = new Date().getTime();
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();

  return Math.min(
    100,
    Math.max(0, Math.floor(((now - startTime) / (endTime - startTime)) * 100))
  );
};

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { user: currentUser } = useAuth();
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState("");
  const [authUserInfo, setAuthUserInfo] = useState<AuthUserInfo | null>(null);
  const [isBanDialogOpen, setIsBanDialogOpen] = useState(false);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
  const [isConfirmEmailDialogOpen, setIsConfirmEmailDialogOpen] =
    useState(false);
  const [isMakingBetaTester, setIsMakingBetaTester] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("auth-info");

  // State for user devices
  const [userDevices, setUserDevices] = useState<
    Array<{
      id: number;
      platform: string;
      device_model: string | null;
      app_version: string | null;
      os_version: string | null;
      is_active: boolean;
      last_used_at: string;
      created_at: string;
      updated_at: string;
    }>
  >([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);

  // State for user push tokens
  const [userPushTokens, setUserPushTokens] = useState<
    Array<{
      id?: number;
      user_id: number;
      push_token: string;
      platform: "ios" | "android" | "web";
      device_model?: string;
      app_version?: string;
      os_version?: string;
      is_active?: boolean;
      last_used_at?: string;
      created_at?: string;
      updated_at?: string;
    }>
  >([]);
  const [isLoadingPushTokens, setIsLoadingPushTokens] = useState(true);

  // New state for user activity statistics
  const [userStats, setUserStats] = useState({
    bookViews: 0,
    bookReads: 0,
    audioPlays: 0,
    totalMinutesRead: 0,
    totalPagesRead: 0,
    favoriteCategories: [] as string[],
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // State for subscription management dialog
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] =
    useState(false);
  // These state variables moved to UserSubscriptionForm component
  // const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  // const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  // const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

  // State for managing roles dialog
  const [isManagingRoles, setIsManagingRoles] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [initialRoleIds, setInitialRoleIds] = useState<number[]>([]);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState<string>("");
  const queryClient = useQueryClient();

  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user", id],
    queryFn: () => fetchUser(parseInt(id || "0", 10)),
  });

  const { data: books, isLoading: isLoadingBooks } = useQuery({
    queryKey: ["books"],
    queryFn: () => fetchBookSummaries(),
  });

  // Fetch all roles to show in the manage roles dialog
  const { data: userRoles = [] } = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
  });

  // Fetch subscription plans
  const { data: subscriptionPlans = [] } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
    enabled: isSubscriptionDialogOpen,
  });

  // Fetch promotions
  const { data: promotions = [] } = useQuery({
    queryKey: ["promotions"],
    queryFn: fetchPromotions,
    enabled: isSubscriptionDialogOpen,
  });

  // Fetch current subscription (active only)
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ["userSubscription", user?.id], // Use user.id (profile ID) instead of user.uid
    queryFn: () => fetchUserSubscription(user?.id?.toString() || ""),
    enabled: !!user?.id,
  });

  // Fetch latest subscription (any status) for admin view
  const { data: latestSubscription, isLoading: isLoadingLatestSubscription } =
    useQuery({
      queryKey: ["userLatestSubscription", user?.id],
      queryFn: () => fetchUserLatestSubscription(user?.id?.toString() || ""),
      enabled: !!user?.id,
    });

  // Fetch subscription history
  const { data: subscriptionHistory = [] } = useQuery({
    queryKey: ["userSubscriptionHistory", user?.id], // Use user.id (profile ID) instead of user.uid
    queryFn: () => fetchUserSubscriptionHistory(user?.id?.toString() || ""),
    enabled: !!user?.id,
  });

  // Check if user is a beta tester
  const { data: isBetaTester = false } = useQuery({
    queryKey: ["userBetaTester", user?.id],
    queryFn: () => isUserBetaTester(user?.id || 0),
    enabled: !!user?.id,
  });

  // Remove the explainer message that we added previously
  useEffect(() => {
    if (user?.id) {
      const getReadingStats = async () => {
        try {
          setIsLoadingStats(true);

          // Use the API function to fetch reading statistics
          const stats = await fetchUserReadingStats(user.id);

          // Fetch user's selected categories from user_categories table
          const [userCategories, userCategoryData] = await Promise.all([
            fetchUserCategories(user.id),
            fetchUserCategoriesWithIds(user.id),
          ]);

          // Update stats with actual user categories only (ignore fallback categories)
          setUserStats({
            ...stats,
            favoriteCategories: userCategories, // Use only actual user categories, empty array if none
          });

          setUserCategoryData(userCategoryData);
        } catch (error) {
          console.error("Failed to fetch user statistics:", error);
          // Set fallback data if an error occurs
          setUserStats({
            bookViews: 0,
            bookReads: 0,
            audioPlays: 0,
            totalMinutesRead: 0,
            totalPagesRead: 0,
            favoriteCategories: [], // Empty array when error occurs
          });
          setUserCategoryData([]);
        } finally {
          setIsLoadingStats(false);
        }
      };

      getReadingStats();
    }
  }, [user?.id]);

  // Fetch payment methods
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["paymentMethods"],
    queryFn: fetchPaymentMethods,
    enabled: isSubscriptionDialogOpen,
  });

  // Fetch currencies
  const { data: currencies = [] } = useQuery({
    queryKey: ["currencies"],
    queryFn: fetchCurrencies,
    enabled: isSubscriptionDialogOpen,
  });

  // Mutation for creating/updating subscription
  const createSubscriptionMutation = useMutation({
    mutationFn: async (subscriptionData: {
      userId: string;
      subscriptionPlanId: number;
      status: string;
      subscriptionStart: string;
      subscriptionEnd: string | null;
      price: number;
      currencyCode: string;
      paymentMethodId: number | null;
      paymentReference?: string | null;
      promotionId?: number | null;
      discountAmount?: number | null;
      autoRenew: boolean;
      deviceType?: string | null;
      osPlatform?: string | null;
      appVersion?: string | null;
      conversionSource?: string | null;
      acquisitionChannel?: string | null;
      previousPlanId?: number | null;
      upgradeDate?: string | null;
      downgradeDate?: string | null;
      cancellationDate?: string | null;
      cancellationReason?: string | null;
      adminNotes?: string | null;
      isTrial?: boolean;
      trialEndDate?: string | null;
      refunded?: boolean;
      refundDate?: string | null;
      refundAmount?: number | null;
    }) => {
      if (!user?.id) throw new Error("User ID is required");

      // Pass the data directly to createUserSubscription
      return createUserSubscription(subscriptionData);
    },
    onSuccess: () => {
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({
        queryKey: ["userSubscription", user?.id], // Use user.id (profile ID) instead of user.uid
      });
      queryClient.invalidateQueries({
        queryKey: ["userLatestSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptionHistory", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptions"],
      });
      toast({
        title: "Subscription Updated",
        description: "The user's subscription has been successfully updated.",
      });
      setIsSubscriptionDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error creating/updating subscription:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Fetch auth user information using the new API function
  useEffect(() => {
    const fetchAuthUserInfo = async () => {
      if (!user?.uid) return;

      try {
        // Only admins or higher can fetch this information
        if (!hasAdminPrivileges()) return;

        const authData = await fetchUserAuthDetails(undefined, user.uid);
        setAuthUserInfo(authData);
      } catch (error) {
        console.error("Error fetching auth user info:", error);
      }
    };

    fetchAuthUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Get role priority - lower number means higher priority
  const getRolePriority = (roleId: number): number => {
    // Define role priorities (1 = highest, 10 = lowest)
    const rolePriorities: Record<number, number> = {
      1: 1, // SuperAdmin (highest priority)
      2: 2, // Admin
      3: 3, // Manager
      4: 4, // Editor
      5: 5, // Standard User
      // Add more role priorities as needed
    };

    return rolePriorities[roleId] || 10; // Default to lowest priority if not found
  };

  // Check if current user has admin privileges to manage roles
  const hasAdminPrivileges = (): boolean => {
    if (!currentUser || !currentUser.roles) return false;

    // Check if the user has any system admin role
    return currentUser.roles.some((role) => role.isSystem === true);
  };

  // Get the current user's highest priority role
  const getCurrentUserHighestRolePriority = (): number => {
    if (
      !currentUser ||
      !currentUser.roleIds ||
      currentUser.roleIds.length === 0
    ) {
      return 100; // Very low priority if no roles
    }

    // Get the lowest priority number (highest priority role)
    return Math.min(...currentUser.roleIds.map(getRolePriority));
  };

  // Check if current user can manage a specific role
  const canManageRole = (roleId: number): boolean => {
    // Admin users can only manage roles with lower priority (higher priority number) than their highest role
    const currentUserHighestPriority = getCurrentUserHighestRolePriority();
    const targetRolePriority = getRolePriority(roleId);

    return currentUserHighestPriority < targetRolePriority;
  };

  // Check if the current user can manage this user's roles
  const canManageUserRoles = (): boolean => {
    if (!user || !user.roleIds || !hasAdminPrivileges()) return false;

    // Check if user has higher priority roles than current user
    const userHighestRolePriority = Math.min(
      ...user.roleIds.map(getRolePriority)
    );
    const currentUserHighestPriority = getCurrentUserHighestRolePriority();

    return currentUserHighestPriority < userHighestRolePriority;
  };

  // Handle role selection
  const handleRoleToggle = (roleId: number) => {
    // Check if user can manage this role
    if (!canManageRole(roleId)) {
      setUnauthorizedMessage("You don't have permission to assign this role.");
      return;
    }

    setUnauthorizedMessage("");
    setSelectedRoleIds((prev) => {
      if (prev.includes(roleId)) {
        return prev.filter((id) => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  // Check if there are any changes in role selection
  const hasRoleChanges = (): boolean => {
    if (selectedRoleIds.length !== initialRoleIds.length) {
      return true;
    }

    return (
      !initialRoleIds.every((id) => selectedRoleIds.includes(id)) ||
      !selectedRoleIds.every((id) => initialRoleIds.includes(id))
    );
  };

  // Handle opening the manage roles dialog
  const handleOpenManageRoles = () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage("Only system administrators can manage user roles.");
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot manage users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    setSelectedRoleIds(user.roleIds || []);
    setInitialRoleIds(user.roleIds || []);
    setUnauthorizedMessage("");
    setIsManagingRoles(true);
  };

  // Handle edit profile button click
  const handleEditProfile = () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can edit user profiles."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to edit a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot edit users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    navigate(`/admin/users/${id}/edit`);
  };

  // Handle change subscription button click
  const handleChangeSubscription = () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can change user subscriptions."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot modify users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Open the subscription dialog
    setIsSubscriptionDialogOpen(true);
  };

  // Handle reset password button click
  const handleResetPassword = () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can reset user passwords."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot reset passwords for users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    setIsResetDialogOpen(true);
  };

  // Add loading states for all buttons
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [isResendingInvitation, setIsResendingInvitation] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [isUnbanning, setIsUnbanning] = useState(false);
  const [isConfirmingEmail, setIsConfirmingEmail] = useState(false);

  // Function to resend invitation email
  const handleResendInvitation = async () => {
    if (!user || !user.email) return;

    try {
      setIsResendingInvitation(true);

      // Use the API function to resend invitation
      await resendInvitation({
        name: user.name,
        email: user.email,
        avatarUrl: user.avatarUrl,
        ageGroup: user.ageGroup,
        isActive: user.isActive,
        roleIds: user.roleIds,
      });

      toast({
        title: "Invitation email sent",
        description: `A new invitation email has been sent to ${user.email}.`,
      });

      // Close the dialog
      setIsInvitationDialogOpen(false);
    } catch (error) {
      console.error("Error sending invitation email:", error);
      toast({
        title: "Error",
        description: "Failed to send invitation email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResendingInvitation(false);
    }
  };

  // Update roles mutation
  const updateRolesMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("No user selected");

      // Double-check permissions before submitting
      if (!hasAdminPrivileges()) {
        throw new Error("You don't have permission to manage user roles");
      }

      // Ensure we're not assigning any roles that the current user can't manage
      const unauthorizedRoles = selectedRoleIds.filter(
        (roleId) => !canManageRole(roleId)
      );

      if (unauthorizedRoles.length > 0) {
        throw new Error(
          "You don't have permission to assign some of these roles"
        );
      }

      // Call the users/update Edge Function using Supabase client
      const { data, error } = await supabase.functions.invoke("users/update", {
        body: {
          id: user.id,
          roleIds: selectedRoleIds,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to update user roles");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      toast({
        title: "Roles updated",
        description: `Roles for ${user?.name} have been updated successfully.`,
      });
      setIsManagingRoles(false);
    },
    onError: (error) => {
      console.error("Error updating user roles:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update user roles. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Save role changes
  const handleSaveRoles = () => {
    if (selectedRoleIds.length === 0) {
      toast({
        title: "Error",
        description: "User must have at least one role assigned.",
        variant: "destructive",
      });
      return;
    }

    updateRolesMutation.mutate();
  };

  // Function to handle sending password reset email
  const handleSendPasswordReset = async () => {
    if (!user || !user.email) return;

    setIsResetting(true);

    try {
      // Use the API function
      await resetUserPassword(user.email);

      toast({
        title: "Password Reset Email Sent",
        description: `A password reset email has been sent to ${user.email}.`,
        duration: 5000,
      });

      // Close the dialog
      setIsResetDialogOpen(false);
    } catch (error) {
      console.error("Error sending password reset:", error);

      toast({
        title: "Error",
        description: "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Add this function to format auth timestamps
  const formatAuthDate = (dateString?: string) => {
    if (!dateString) return "Never";
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
  };

  // Function to manually confirm user's email
  const handleConfirmEmail = async () => {
    if (!user || !user.uid) return;

    try {
      setIsConfirmingEmail(true);

      // Use the API function
      await confirmUserEmail(user.uid);

      // Refetch the auth user info to update the UI
      const authData = await fetchUserAuthDetails(undefined, user.uid);
      setAuthUserInfo(authData);

      toast({
        title: "Email confirmed",
        description: "User's email address has been manually confirmed.",
      });

      // Close the dialog
      setIsConfirmEmailDialogOpen(false);
    } catch (error) {
      console.error("Error confirming email:", error);
      toast({
        title: "Error",
        description: "Failed to confirm email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsConfirmingEmail(false);
    }
  };

  // Function to open resend OTP dialog
  const handleResendOTPDialog = () => {
    if (!user || !user.email) return;
    setIsOtpDialogOpen(true);
  };

  // Function to resend OTP (update name and message)
  const handleResendOTP = async () => {
    if (!user || !user.email) return;

    try {
      setIsResendingConfirmation(true);

      // Use the API function
      await resendConfirmationEmail(user.email);

      toast({
        title: "OTP sent",
        description: `A verification OTP has been sent to ${user.email}.`,
      });

      // Close the dialog
      setIsOtpDialogOpen(false);
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast({
        title: "Error",
        description: "Failed to send verification OTP. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsResendingConfirmation(false);
    }
  };

  // Function to open resend invitation dialog
  const handleResendInvitationDialog = () => {
    if (!user || !user.email) return;
    setIsInvitationDialogOpen(true);
  };

  // Function to open confirm email dialog
  const handleConfirmEmailDialog = () => {
    if (!user || !user.uid) return;
    setIsConfirmEmailDialogOpen(true);
  };

  // Purge user data permanently
  const handlePurgeUserData = async () => {
    if (!user?.id) return;
    try {
      setIsPurging(true);
      const { data, error } = await supabase.functions.invoke("users/purge", {
        body: { id: user.id },
      });
      if (error) throw error;
      toast({
        title: "User data purged",
        description: `${user.name}'s data has been permanently deleted.`,
      });
      setIsPurgeDialogOpen(false);
      // After purge, navigate back to users list
      navigate("/admin/users");
    } catch (err) {
      console.error("Error purging user data:", err);
      toast({
        title: "Error",
        description: "Failed to purge user data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsPurging(false);
    }
  };

  // Handle make user beta tester
  const handleMakeBetaTester = async () => {
    if (!user?.id) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can make users beta testers."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot manage users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    setIsMakingBetaTester(true);
    try {
      const result = await makeUserBetaTester(user.id);

      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
        });
        // Refetch beta tester status
        queryClient.invalidateQueries({
          queryKey: ["userBetaTester", user.id],
        });
      } else {
        toast({
          title: "Error",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error making user beta tester:", error);
      toast({
        title: "Error",
        description: "Failed to make user a beta tester. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsMakingBetaTester(false);
    }
  };

  // Handle ban user account button click (update with loading state)
  const handleBanAccount = async () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage("Only system administrators can ban user accounts.");
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot ban users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Open confirmation dialog instead of immediately banning
    setIsBanDialogOpen(true);
  };

  // Confirm ban/unban action
  const confirmBanUnbanAction = async () => {
    if (!user) return;

    try {
      if (user.isActive) {
        setIsBanning(true);
        await banUser(user.id);
        toast({
          title: "User banned",
          description: `${user.name} has been banned successfully.`,
        });
      } else {
        setIsUnbanning(true);
        await unbanUser(user.id);
        toast({
          title: "User unbanned",
          description: `${user.name} has been unbanned successfully.`,
        });
      }

      // Refresh user data
      queryClient.invalidateQueries({ queryKey: ["user", id] });

      // Close the dialog
      setIsBanDialogOpen(false);
    } catch (error) {
      console.error(
        `Error ${user.isActive ? "banning" : "unbanning"} user:`,
        error
      );
      toast({
        title: "Error",
        description: `Failed to ${
          user.isActive ? "ban" : "unban"
        } user. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsBanning(false);
      setIsUnbanning(false);
    }
  };

  // Handle unban user account button click (update with loading state)
  const handleUnbanAccount = async () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can unban user accounts."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot unban users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Open confirmation dialog instead of immediately unbanning
    setIsBanDialogOpen(true);
  };

  // Handle delete user account button click
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteAdditionalFeedback, setDeleteAdditionalFeedback] = useState("");
  // New: purge dialog state
  const [isPurgeDialogOpen, setIsPurgeDialogOpen] = useState(false);
  const [isPurging, setIsPurging] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user) return;

    // Check if the current user has admin privileges
    if (!hasAdminPrivileges()) {
      setPermissionMessage(
        "Only system administrators can delete user accounts."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Check if trying to manage a user with higher priority roles
    if (!canManageUserRoles()) {
      setPermissionMessage(
        "You cannot delete users with equal or higher role priority than yours."
      );
      setPermissionDialogOpen(true);
      return;
    }

    // Open confirmation dialog
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteAccount = async () => {
    if (!user) return;

    // Validate required fields
    if (!deleteReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for deleting this account.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDeleting(true);

      // Use the API function with feedback
      await deleteUser(user.id, deleteReason, deleteAdditionalFeedback);

      toast({
        title: "User deleted",
        description: `${user.name} has been deleted successfully.`,
      });

      // Close the dialog and navigate back to users list
      setIsDeleteDialogOpen(false);
      navigate("/admin/users");
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Function to handle subscription form submission
  const handleSubscriptionSubmit = (data: SubscriptionFormData) => {
    try {
      if (!user?.id) {
        toast({
          title: "Error",
          description: "User ID is required",
          variant: "destructive",
        });
        return;
      }

      // Handle special case for "none" payment method
      // Make sure it's truly null when "none" is selected
      const paymentMethodId =
        data.paymentMethodId === "none" || data.paymentMethodId === null
          ? null
          : typeof data.paymentMethodId === "string"
          ? parseInt(data.paymentMethodId, 10)
          : data.paymentMethodId;

      // Calculate subscription end date from plan duration
      const selectedPlan = subscriptionPlans.find((p) => p.id === data.planId);
      const endDate = selectedPlan
        ? formatISO(
            selectedPlan.durationType === "days"
              ? addDays(new Date(), selectedPlan.duration)
              : selectedPlan.durationType === "months"
              ? addMonths(new Date(), selectedPlan.duration)
              : addYears(new Date(), selectedPlan.duration)
          )
        : null;

      // Determine if this is an upgrade or downgrade
      const isUpgrade =
        currentSubscription &&
        data.planId !== currentSubscription.subscriptionPlanId &&
        data.price > currentSubscription.price;

      const isDowngrade =
        currentSubscription &&
        data.planId !== currentSubscription.subscriptionPlanId &&
        data.price <= currentSubscription.price;

      // Create subscription data object aligned with our new table structure
      const subscriptionData = {
        // Essential fields - use the profile ID (numeric) instead of auth.user.uid (UUID)
        userId: user.id.toString(), // Convert to string as the API expects a string
        subscriptionPlanId: data.planId,
        status: "active",
        subscriptionStart: formatISO(new Date()),
        subscriptionEnd: endDate,

        // Payment details
        price: data.price, // Use the price from form which has discount applied
        currencyCode: data.currencyCode,
        paymentMethodId,
        paymentReference: null,
        promotionId:
          data.promotionId && data.promotionId > 0 ? data.promotionId : null,
        // Use the discount values directly from the form
        // promoDiscount only contains discount from promo codes
        promoDiscount:
          data.promoDiscount && data.promoDiscount > 0
            ? data.promoDiscount
            : null,
        // referralDiscount contains discount from referral
        referralDiscount:
          data.referralDiscount && data.referralDiscount > 0
            ? data.referralDiscount
            : null,
        // referrerId only if referral discount is applied
        referrerId:
          data.useReferralDiscount &&
          data.referralDiscount &&
          data.referralDiscount > 0
            ? user.referredBy
            : null,
        // totalDiscount contains all discounts (promo + plan + referral)
        totalDiscount:
          data.totalDiscount && data.totalDiscount > 0
            ? data.totalDiscount
            : null,

        // Subscription options
        autoRenew: data.autoRenew || false,

        // Metadata - platform info
        deviceType: "admin-panel",
        osPlatform: "web",
        appVersion: "1.0",
        conversionSource: "admin_panel",
        acquisitionChannel: data.promoCode
          ? "promo_code"
          : data.useReferralDiscount
          ? "referral_discount"
          : "manual_assignment",
        previousPlanId: currentSubscription?.subscriptionPlanId || null,

        // Subscription events
        upgradeDate: isUpgrade ? formatISO(new Date()) : null,
        downgradeDate: isDowngrade ? formatISO(new Date()) : null,
        cancellationDate: null,
        cancellationReason: null,

        // Administrative
        adminNotes: data.adminNotes || "",
        isTrial: false,
        trialEndDate: null,
        refunded: false,
        refundDate: null,
        refundAmount: null,
      };

      // console.log("Submitting subscription data:", subscriptionData);
      createSubscriptionMutation.mutate(subscriptionData);
    } catch (error) {
      console.error("Error preparing subscription data:", error);
      toast({
        title: "Error",
        description: "Failed to prepare subscription data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Function to handle tab switching programmatically
  const switchToTab = (tabValue: string) => {
    setActiveTab(tabValue);
    // Find the tab element and trigger a click if it exists
    setTimeout(() => {
      const tabElement = document.querySelector(
        `[data-state][data-value="${tabValue}"]`
      );
      if (tabElement instanceof HTMLElement) {
        tabElement.click();
      }
    }, 0);
  };

  // Add state for subscription management dialogs
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

  // Add mutations for subscription actions
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (data: {
      cancellationReason: string;
      immediateEffect: boolean;
      issueRefund: boolean;
      refundAmount: number | null;
      adminNotes: string;
    }) => {
      if (!user?.id || !currentSubscription?.id)
        throw new Error("User ID and subscription ID are required");

      // Always set status to cancelled when cancelling, regardless of immediate effect
      const status = "cancelled";

      // Update the subscription with cancellation info
      const updateData: SubscriptionCancelUpdateData = {
        status,
        cancellationReason: data.cancellationReason,
        cancellationDate: new Date().toISOString(),
      };

      // If immediate effect is requested, set subscription end to now
      if (data.immediateEffect) {
        updateData.subscriptionEnd = new Date().toISOString();
      }

      // Only add refund fields if refund is being issued
      if (data.issueRefund) {
        updateData.refunded = true;
        updateData.refundDate = new Date().toISOString();
        updateData.refundAmount = data.refundAmount;
      }

      // Append admin notes if provided
      if (data.adminNotes && data.adminNotes.trim()) {
        const existingNotes = currentSubscription.adminNotes || "";
        const newNote = `[${
          new Date().toISOString().split("T")[0]
        }] Cancelled: ${data.adminNotes}`;
        updateData.adminNotes = existingNotes
          ? `${existingNotes}\n${newNote}`
          : newNote;
      }

      // console.log("Cancelling subscription with data:", updateData);
      return updateUserSubscription(currentSubscription.id, updateData);
    },
    onSuccess: (result) => {
      // console.log("Subscription cancelled successfully:", result);
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({
        queryKey: ["userSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userLatestSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptionHistory", user?.id],
      });
      // Also invalidate the general user subscriptions query for dashboard/list views
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptions"],
      });
      toast({
        title: "Subscription Cancelled",
        description: "The subscription has been cancelled successfully.",
      });
      setIsCancelDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error cancelling subscription:", error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const extendSubscriptionMutation = useMutation({
    mutationFn: async (data: {
      extensionPeriod: string;
      customDays: number | null;
      chargeAdditional: boolean;
      additionalCharge: number | null;
      adminNotes: string;
      newEndDate: Date;
    }) => {
      if (!user?.id || !currentSubscription?.id)
        throw new Error("User ID and subscription ID are required");

      // Update the subscription with new end date
      const updateData: SubscriptionExtendUpdateData = {
        subscriptionEnd: data.newEndDate.toISOString(),
      };

      // Append admin notes if provided
      if (data.adminNotes && data.adminNotes.trim()) {
        const existingNotes = currentSubscription.adminNotes || "";
        const newNote = `[${
          new Date().toISOString().split("T")[0]
        }] Extended: ${data.adminNotes}`;
        updateData.adminNotes = existingNotes
          ? `${existingNotes}\n${newNote}`
          : newNote;
      }

      return updateUserSubscription(currentSubscription.id, updateData);
    },
    onSuccess: () => {
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({
        queryKey: ["userSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userLatestSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptionHistory", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptions"],
      });
      toast({
        title: "Subscription Extended",
        description: "The subscription has been extended successfully.",
      });
      setIsExtendDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error extending subscription:", error);
      toast({
        title: "Error",
        description: "Failed to extend subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (data: {
      newStatus: "active" | "cancelled" | "expired" | "pending";
      adminNotes: string;
    }) => {
      if (!user?.id || !currentSubscription?.id)
        throw new Error("User ID and subscription ID are required");

      // Update the subscription status
      const updateData: SubscriptionStatusUpdateData = {
        status: data.newStatus,
      };

      // Append admin notes if provided
      if (data.adminNotes && data.adminNotes.trim()) {
        const existingNotes = currentSubscription.adminNotes || "";
        const newNote = `[${
          new Date().toISOString().split("T")[0]
        }] Status changed to ${data.newStatus}: ${data.adminNotes}`;
        updateData.adminNotes = existingNotes
          ? `${existingNotes}\n${newNote}`
          : newNote;
      }

      return updateUserSubscription(currentSubscription.id, updateData);
    },
    onSuccess: () => {
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({
        queryKey: ["userSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userLatestSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptionHistory", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptions"],
      });
      toast({
        title: "Status Updated",
        description: "The subscription status has been updated successfully.",
      });
      setIsStatusDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error changing subscription status:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Add handlers for subscription actions
  const handleCancelSubscription = () => {
    if (!currentSubscription) return;
    setIsCancelDialogOpen(true);
  };

  const handleExtendSubscription = () => {
    if (!currentSubscription) return;
    setIsExtendDialogOpen(true);
  };

  const handleChangeStatus = () => {
    if (!currentSubscription) return;
    setIsStatusDialogOpen(true);
  };

  // Add state for upgrade subscription dialog
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);

  // Add mutation for upgrade subscription
  const upgradeSubscriptionMutation = useMutation({
    mutationFn: async (data: {
      newPlanId: number;
      applyProrating: boolean;
      proratedCredit: number | null;
      newPrice: number;
      newCurrencyCode: string;
      newEndDate: Date | null;
      adminNotes: string;
      keepCurrentEndDate: boolean;
    }) => {
      if (!user?.id || !currentSubscription?.id)
        throw new Error("User ID and subscription ID are required");

      // Prepare data for API call
      const isUpgrade = data.newPrice > currentSubscription.price;
      const isDowngrade = data.newPrice < currentSubscription.price;

      // For audit trail, store the previous plan details
      const previousPlanId = currentSubscription.subscriptionPlanId;

      // Create a new subscription record
      return createUserSubscription({
        userId: user.id.toString(),
        subscriptionPlanId: data.newPlanId,
        status: "active",
        subscriptionStart: formatISO(new Date()),
        subscriptionEnd: data.newEndDate ? formatISO(data.newEndDate) : null,
        price: data.newPrice,
        currencyCode: data.newCurrencyCode,
        paymentMethodId: currentSubscription.paymentMethodId,
        // When upgrading, the totalDiscount is the prorated credit (if any)
        // This is a different flow than regular subscription creation
        totalDiscount: data.applyProrating ? data.proratedCredit : 0,
        // No promo discount when manually upgrading a subscription
        promoDiscount: 0,
        autoRenew: currentSubscription.autoRenew,
        previousPlanId: previousPlanId,
        upgradeDate: isUpgrade ? formatISO(new Date()) : null,
        downgradeDate: isDowngrade ? formatISO(new Date()) : null,
        adminNotes: currentSubscription.adminNotes
          ? `${currentSubscription.adminNotes}\n[${
              new Date().toISOString().split("T")[0]
            }] Plan changed: ${data.adminNotes}`
          : `[${new Date().toISOString().split("T")[0]}] Plan changed: ${
              data.adminNotes
            }`,
        deviceType: "admin-panel",
        osPlatform: "web",
        appVersion: "1.0",
        conversionSource: "admin_panel",
        acquisitionChannel: "manual_plan_change",
      });
    },
    onSuccess: () => {
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({
        queryKey: ["userSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userLatestSubscription", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptionHistory", user?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["userSubscriptions"],
      });
      toast({
        title: "Subscription Updated",
        description: "The subscription plan has been changed successfully.",
      });
      setIsUpgradeDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error upgrading subscription:", error);
      toast({
        title: "Error",
        description: "Failed to change subscription plan. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Add handler for upgrade subscription
  const handleUpgradeSubscription = () => {
    if (!currentSubscription) return;
    setIsUpgradeDialogOpen(true);
  };

  // Function to open category picker
  const handleOpenCategoryPicker = async () => {
    setSelectedCategoryIds([]);
    // Refresh user categories to ensure we have the latest data
    await refreshUserCategories();
    setShowCategoryPicker(true);
  };

  // Function to refresh user categories
  const refreshUserCategories = async () => {
    if (!user?.id) return;

    try {
      console.log("Refreshing categories for user:", user.id);

      // Fetch both the category names for display and the full data for removal
      const [categoryNames, categoryData] = await Promise.all([
        fetchUserCategories(user.id),
        fetchUserCategoriesWithIds(user.id),
      ]);

      console.log("Refreshed category names:", categoryNames);
      console.log("Refreshed category data:", categoryData);

      setUserStats((prev) => ({
        ...prev,
        favoriteCategories: categoryNames,
      }));

      setUserCategoryData(categoryData);
    } catch (error) {
      console.error("Error refreshing user categories:", error);
      toast({
        title: "Warning",
        description:
          "Failed to refresh category display. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  // Function to add selected categories
  const handleAddSelectedCategories = async () => {
    if (!user?.id || selectedCategoryIds.length === 0) return;

    try {
      setIsAddingCategories(true);

      console.log(
        "Adding categories:",
        selectedCategoryIds,
        "for user:",
        user.id
      );
      await addUserCategories(user.id, selectedCategoryIds);

      // Add a small delay to ensure the database has been updated
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refresh the user categories
      await refreshUserCategories();

      toast({
        title: "Categories Added",
        description: `${selectedCategoryIds.length} categories have been added successfully.`,
      });

      setShowCategoryPicker(false);
      setSelectedCategoryIds([]);
    } catch (error) {
      console.error("Error adding selected categories:", error);
      toast({
        title: "Error",
        description: "Failed to add categories. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingCategories(false);
    }
  };

  // Function to remove individual category
  const handleRemoveCategory = async (
    categoryId: number,
    categoryName: string
  ) => {
    if (!user?.id) return;

    try {
      setIsAddingCategories(true);

      console.log("Removing category:", categoryId, "for user:", user.id);
      await removeUserCategory(user.id, categoryId);

      // Add a small delay to ensure the database has been updated
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Refresh the user categories
      await refreshUserCategories();

      toast({
        title: "Category Removed",
        description: `"${categoryName}" has been removed successfully.`,
      });
    } catch (error) {
      console.error("Error removing category:", error);
      toast({
        title: "Error",
        description: "Failed to remove category. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingCategories(false);
    }
  };

  // Add collaborator state
  const [collaboratorData, setCollaboratorData] = useState<
    CollaboratorPromotion[] | null
  >(null);
  const [isLoadingCollaboratorData, setIsLoadingCollaboratorData] =
    useState(true);
  const [collaboratorStats, setCollaboratorStats] = useState<{
    totalEarnings: number;
    pendingEarnings: number;
    activePromotions: number;
    totalSubscriptions: number;
  }>({
    totalEarnings: 0,
    pendingEarnings: 0,
    activePromotions: 0,
    totalSubscriptions: 0,
  });

  // State for managing categories
  const [isAddingCategories, setIsAddingCategories] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  const [userCategoryData, setUserCategoryData] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // State for account deletion feedback
  const [deletionFeedback, setDeletionFeedback] =
    useState<AccountDeletionFeedback | null>(null);
  const [isLoadingDeletionFeedback, setIsLoadingDeletionFeedback] =
    useState(false);

  // Add a function to fetch collaborator data
  const fetchCollaboratorData = async () => {
    if (!user?.id) return;

    try {
      setIsLoadingCollaboratorData(true);

      // Check if user is a collaborator on any promotions
      const { data, error } = await supabase
        .from("promotions")
        .select("*")
        .eq("collaborator_id", user.id);

      if (error) throw error;

      setCollaboratorData(data || []);

      // Calculate basic stats
      if (data && data.length > 0) {
        // Fetch earnings to calculate totals
        const { data: earningsData, error: earningsError } = await supabase
          .from("collaborator_earnings")
          .select("amount_earned, payout_id")
          .eq("collaborator_id", user.id);

        if (earningsError) throw earningsError;

        const totalEarnings = earningsData
          ? earningsData.reduce(
              (sum, item) => sum + (item.amount_earned || 0),
              0
            )
          : 0;

        const pendingEarnings = earningsData
          ? earningsData
              .filter((item) => !item.payout_id)
              .reduce((sum, item) => sum + (item.amount_earned || 0), 0)
          : 0;

        setCollaboratorStats({
          totalEarnings,
          pendingEarnings,
          activePromotions: data.filter((p) => p.is_active).length,
          totalSubscriptions: earningsData?.length || 0,
        });
      }

      setIsLoadingCollaboratorData(false);
    } catch (error) {
      console.error("Error fetching collaborator data:", error);
      setIsLoadingCollaboratorData(false);
    }
  };

  // Load collaborator data when component mounts
  useEffect(() => {
    if (user?.id) {
      fetchCollaboratorData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load available categories when component mounts
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await fetchAllCategories();
        setAvailableCategories(categories);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };
    loadCategories();
  }, []);

  // Fetch deletion feedback for inactive users
  useEffect(() => {
    const loadDeletionFeedback = async () => {
      if (!user || user.isActive) {
        setDeletionFeedback(null);
        return;
      }

      try {
        setIsLoadingDeletionFeedback(true);
        const feedback = await fetchUserDeletionFeedback(user.id, user.uid);
        setDeletionFeedback(feedback);
      } catch (error) {
        console.error("Error loading deletion feedback:", error);
        setDeletionFeedback(null);
      } finally {
        setIsLoadingDeletionFeedback(false);
      }
    };

    loadDeletionFeedback();
  }, [user?.id, user?.isActive, user?.uid]);

  // Fetch user devices
  useEffect(() => {
    const loadUserDevices = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingDevices(true);
        const devices = await fetchUserDevices(user.id);
        setUserDevices(devices);
      } catch (error) {
        console.error("Error loading user devices:", error);
        setUserDevices([]);
      } finally {
        setIsLoadingDevices(false);
      }
    };

    loadUserDevices();
  }, [user?.id]);

  // Fetch user push tokens
  useEffect(() => {
    const loadUserPushTokens = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingPushTokens(true);
        const tokens = await getUserPushTokens(user.id);
        setUserPushTokens(tokens);
      } catch (error) {
        console.error("Error loading user push tokens:", error);
        setUserPushTokens([]);
      } finally {
        setIsLoadingPushTokens(false);
      }
    };

    loadUserPushTokens();
  }, [user?.id]);

  if (isLoadingUser || isLoadingBooks || !user || !books) {
    return (
      <AdminLayout title="User Details">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  const getSubscriptionStatusColor = (isActive: boolean) => {
    return isActive
      ? "bg-green-500 hover:bg-green-600"
      : "bg-gray-500 hover:bg-gray-600";
  };

  // Determine effective status based on end date
  const getEffectiveStatus = (
    status?: string,
    subscriptionEnd?: string | null
  ): "active" | "cancelled" | "expired" | "pending" | "unknown" => {
    // If explicitly cancelled, expired, or pending, preserve that status
    if (
      status === "cancelled" ||
      status === "expired" ||
      status === "pending"
    ) {
      return status;
    }

    // Only override "active" status if end date has passed
    if (status === "active" && subscriptionEnd) {
      const end = new Date(subscriptionEnd);
      if (!isNaN(end.getTime()) && end < new Date()) {
        return "expired";
      }
    }

    // Return the original status or default to unknown
    if (status === "active") {
      return status;
    }
    return "unknown";
  };

  // Helper function to get the correct user ID for subscriptions
  // We should use user.id (converted to string) from the profiles table
  // instead of the auth uid
  const getUserIdForSubscription = () => {
    if (!user) return null;
    return user.id.toString();
  };

  // Add this helper function for formatting provider names
  const formatProviderName = (provider: string): string => {
    switch (provider) {
      case "email":
        return "Email";
      case "google":
        return "Google";
      case "facebook":
        return "Facebook";
      case "twitter":
        return "Twitter";
      case "apple":
        return "Apple";
      case "github":
        return "GitHub";
      case "azure":
        return "Microsoft";
      case "linkedin":
        return "LinkedIn";
      case "discord":
        return "Discord";
      case "slack":
        return "Slack";
      default:
        // Capitalize the first letter
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  // Add this helper function to get provider icons
  const getProviderIcon = (
    provider: string,
    className = "h-4 w-4 mr-2"
  ): JSX.Element | null => {
    switch (provider) {
      case "email":
        return <Mail className={className} />;
      case "phone":
        return <Phone className={className} />;
      case "google":
        return <div className={className}>G</div>;
      case "facebook":
        return <div className={className}>f</div>;
      case "twitter":
        return <div className={className}>𝕏</div>;
      case "apple":
        return <div className={className}>⌘</div>;
      case "github":
        return <div className={className}>⎇</div>;
      default:
        return <Key className={className} />;
    }
  };

  return (
    <AdminLayout title="User Details">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">User Profile</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <UserProfileHeader
            user={user}
            authUserInfo={authUserInfo}
            currentSubscription={currentSubscription}
            latestSubscription={latestSubscription}
            isLoadingSubscription={isLoadingSubscription}
            onSwitchToTab={switchToTab}
            deletionFeedback={deletionFeedback}
            isLoadingDeletionFeedback={isLoadingDeletionFeedback}
            isBetaTester={isBetaTester}
          />

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value)}
            defaultValue={activeTab}
          >
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
              <TabsTrigger value="auth-info">Auth Details</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="referrals">Referrals</TabsTrigger>
              <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>

            <TabsContent value="activity" className="mt-4">
              <UserActivityFeed userId={user.id} books={books?.data || []} />
            </TabsContent>

            <TabsContent value="auth-info" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Authentication Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hasAdminPrivileges() ? (
                    <div className="space-y-6">
                      {/* Account Status & Important Dates */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Account Status Card */}
                        <Card className="border-l-4 border-l-blue-500">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Fingerprint className="h-4 w-4" />
                              Account Status
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 gap-2">
                              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Email Confirmed
                                  </span>
                                </div>
                                {authUserInfo?.email_confirmed_at ? (
                                  <Badge className="bg-green-500 hover:bg-green-600">
                                    Confirmed
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-500 hover:bg-amber-600">
                                    Pending
                                  </Badge>
                                )}
                              </div>

                              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Phone Confirmed
                                  </span>
                                </div>
                                {authUserInfo?.phone_confirmed_at ? (
                                  <Badge className="bg-green-500 hover:bg-green-600">
                                    Confirmed
                                  </Badge>
                                ) : authUserInfo?.phone ? (
                                  <Badge className="bg-amber-500 hover:bg-amber-600">
                                    Pending
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="border-gray-300 text-gray-500"
                                  >
                                    Not Provided
                                  </Badge>
                                )}
                              </div>

                              <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Account Type
                                  </span>
                                </div>
                                {authUserInfo?.is_anonymous ? (
                                  <Badge className="bg-blue-500 hover:bg-blue-600">
                                    Anonymous
                                  </Badge>
                                ) : (
                                  <Badge className="bg-green-500 hover:bg-green-600">
                                    Registered
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Important Dates Card */}
                        <Card className="border-l-4 border-l-green-500">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              Important Dates
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 gap-2">
                              <div className="p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Account Created
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {formatAuthDate(authUserInfo?.created_at)}
                                </p>
                              </div>

                              <div className="p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <LogIn className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Last Sign In
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {formatAuthDate(
                                    authUserInfo?.last_sign_in_at
                                  )}
                                </p>
                              </div>

                              <div className="p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Email Confirmed
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {formatAuthDate(
                                    authUserInfo?.email_confirmed_at
                                  )}
                                </p>
                              </div>
                              <div className="p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Last Updated
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {formatAuthDate(authUserInfo?.updated_at)}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Authentication Information Card */}
                      <Card className="border-l-4 border-l-purple-500">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            Authentication Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {authUserInfo?.phone && (
                              <div className="p-2 bg-muted/30 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Phone Number
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground font-mono">
                                  {authUserInfo.phone}
                                </p>
                              </div>
                            )}

                            <div className="p-2 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <Hash className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  User ID
                                </span>
                              </div>
                              <p className="text-xs font-mono bg-background p-2 rounded border">
                                {user?.uid}
                              </p>
                            </div>

                            <div className="p-2 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  Audience
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {authUserInfo?.aud || "authenticated"}
                              </p>
                            </div>

                            <div className="p-2 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <Shield className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  Database Role
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-muted-foreground">
                                  {authUserInfo?.role || "authenticated"}
                                </p>
                                {authUserInfo?.role === "authenticated" && (
                                  <Badge variant="outline" className="text-xs">
                                    Default
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Authentication Providers Card */}
                      {authUserInfo?.identities &&
                        authUserInfo.identities.length > 0 && (
                          <Card className="border-l-4 border-l-orange-500">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2">
                                <Key className="h-4 w-4" />
                                Authentication Providers
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              {/* Primary Provider */}
                              {authUserInfo.app_metadata?.provider && (
                                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      {getProviderIcon(
                                        authUserInfo.app_metadata.provider
                                      )}
                                      <span className="font-medium">
                                        {formatProviderName(
                                          authUserInfo.app_metadata.provider
                                        )}
                                      </span>
                                      <Badge className="bg-primary/20 text-primary text-xs">
                                        Primary
                                      </Badge>
                                    </div>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    First sign-up method
                                  </p>
                                </div>
                              )}

                              {/* All Connected Providers */}
                              <div className="space-y-2">
                                {authUserInfo.identities.map(
                                  (identity, index) => (
                                    <div
                                      key={index}
                                      className={cn(
                                        "p-3 rounded-lg border transition-colors",
                                        authUserInfo.app_metadata?.provider ===
                                          identity.provider
                                          ? "border-primary/20 bg-primary/5"
                                          : "border-muted bg-muted/30"
                                      )}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          {getProviderIcon(identity.provider)}
                                          <span className="font-medium">
                                            {formatProviderName(
                                              identity.provider
                                            )}
                                          </span>
                                        </div>
                                        <Badge
                                          variant="outline"
                                          className="text-xs font-mono"
                                        >
                                          {identity.id.length > 20
                                            ? identity.id.substring(0, 10) +
                                              "..." +
                                              identity.id.substring(
                                                identity.id.length - 10
                                              )
                                            : identity.id}
                                        </Badge>
                                      </div>

                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                        <div className="flex items-center gap-2">
                                          <Clock className="h-3 w-3 text-muted-foreground" />
                                          <span className="text-muted-foreground">
                                            Connected:
                                          </span>
                                          <span className="font-medium">
                                            {formatAuthDate(
                                              identity.created_at
                                            )}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <RefreshCw className="h-3 w-3 text-muted-foreground" />
                                          <span className="text-muted-foreground">
                                            Updated:
                                          </span>
                                          <span className="font-medium">
                                            {formatAuthDate(
                                              identity.updated_at
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>

                              {/* Available Sign-in Methods */}
                              {authUserInfo.app_metadata?.providers &&
                                Array.isArray(
                                  authUserInfo.app_metadata.providers
                                ) &&
                                authUserInfo.app_metadata.providers.length >
                                  0 && (
                                  <div className="pt-2 border-t">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Users className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        Available Sign-in Methods
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {authUserInfo.app_metadata.providers.map(
                                        (provider) => (
                                          <Badge
                                            key={provider}
                                            variant="secondary"
                                            className="flex items-center gap-1"
                                          >
                                            {getProviderIcon(
                                              provider,
                                              "h-3 w-3"
                                            )}
                                            {formatProviderName(provider)}
                                          </Badge>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                            </CardContent>
                          </Card>
                        )}

                      <div>
                        <h3 className="text-base font-medium mb-2">
                          Device Information
                        </h3>
                        {isLoadingDevices ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </div>
                        ) : userDevices.length === 0 ? (
                          <div className="text-center py-4 bg-muted/30 rounded-lg">
                            <p className="text-muted-foreground">
                              No devices registered for this user
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Device Summary */}
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-primary">
                                  {userDevices.length}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Total Devices
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                  {
                                    userDevices.filter((d) => d.is_active)
                                      .length
                                  }
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Active Devices
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                  {
                                    new Set(userDevices.map((d) => d.platform))
                                      .size
                                  }
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Platforms
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600">
                                  {userPushTokens.length}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Push Tokens
                                </div>
                              </div>
                            </div>

                            {/* Device List */}
                            <div className="space-y-3">
                              {userDevices.map((device) => (
                                <div
                                  key={device.id}
                                  className={cn(
                                    "border rounded-lg p-4 transition-colors",
                                    device.is_active
                                      ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                                      : "bg-muted/30 border-muted"
                                  )}
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          variant="outline"
                                          className={cn(
                                            "capitalize",
                                            device.platform === "ios" &&
                                              "border-blue-300 text-blue-700 dark:text-blue-300",
                                            device.platform === "android" &&
                                              "border-green-300 text-green-700 dark:text-green-300",
                                            device.platform === "web" &&
                                              "border-purple-300 text-purple-700 dark:text-purple-300"
                                          )}
                                        >
                                          {device.platform}
                                        </Badge>
                                        {device.is_active && (
                                          <Badge className="bg-green-500 hover:bg-green-600 text-white">
                                            Active
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right text-sm text-muted-foreground">
                                      <div>Last used</div>
                                      <div className="font-medium">
                                        {formatAuthDate(device.last_used_at)}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                    {device.device_model && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Device:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {device.device_model}
                                        </span>
                                      </div>
                                    )}
                                    {device.app_version && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          App Version:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {device.app_version}
                                        </span>
                                      </div>
                                    )}
                                    {device.os_version && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          OS Version:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {device.os_version}
                                        </span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">
                                        Registered:
                                      </span>{" "}
                                      <span className="font-medium">
                                        {formatAuthDate(device.created_at)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">
                                        Push Token:
                                      </span>{" "}
                                      <span className="font-medium">
                                        {userPushTokens.some(
                                          (token) =>
                                            token.platform ===
                                              device.platform && token.is_active
                                        ) ? (
                                          <Badge className="bg-green-500 hover:bg-green-600 text-white text-xs">
                                            Active
                                          </Badge>
                                        ) : (
                                          <Badge
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            None
                                          </Badge>
                                        )}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">
                                        Updated:
                                      </span>{" "}
                                      <span className="font-medium">
                                        {formatAuthDate(device.updated_at)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* App Version Distribution */}
                            {userDevices.some((d) => d.app_version) && (
                              <div>
                                <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                  App Version Distribution
                                </h4>
                                <div className="space-y-2">
                                  {Object.entries(
                                    userDevices
                                      .filter((d) => d.app_version)
                                      .reduce((acc, device) => {
                                        const version = device.app_version!;
                                        acc[version] = (acc[version] || 0) + 1;
                                        return acc;
                                      }, {} as Record<string, number>)
                                  )
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([version, count]) => (
                                      <div
                                        key={version}
                                        className="flex items-center justify-between p-2 bg-muted/30 rounded"
                                      >
                                        <span className="font-medium">
                                          {version}
                                        </span>
                                        <Badge variant="secondary">
                                          {count} device{count > 1 ? "s" : ""}
                                        </Badge>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            {/* Platform Distribution */}
                            <div>
                              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                Platform Distribution
                              </h4>
                              <div className="space-y-2">
                                {Object.entries(
                                  userDevices.reduce((acc, device) => {
                                    acc[device.platform] =
                                      (acc[device.platform] || 0) + 1;
                                    return acc;
                                  }, {} as Record<string, number>)
                                )
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([platform, count]) => (
                                    <div
                                      key={platform}
                                      className="flex items-center justify-between p-2 bg-muted/30 rounded"
                                    >
                                      <span className="font-medium capitalize">
                                        {platform}
                                      </span>
                                      <Badge variant="secondary">
                                        {count} device{count > 1 ? "s" : ""}
                                      </Badge>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Push Tokens Section */}
                      <Card className="border-l-4 border-l-purple-500">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Key className="h-5 w-5" />
                            Push Tokens
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {isLoadingPushTokens ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                          ) : userPushTokens.length === 0 ? (
                            <div className="text-center py-4 bg-muted/30 rounded-lg">
                              <p className="text-muted-foreground">
                                No push tokens registered for this user
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Push Token Summary */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-primary">
                                    {userPushTokens.length}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Total Tokens
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    {
                                      userPushTokens.filter((t) => t.is_active)
                                        .length
                                    }
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Active Tokens
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-blue-600">
                                    {
                                      new Set(
                                        userPushTokens.map((t) => t.platform)
                                      ).size
                                    }
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Platforms
                                  </div>
                                </div>
                              </div>

                              {/* Push Token List */}
                              <div className="space-y-3">
                                {userPushTokens.map((token) => (
                                  <div
                                    key={token.id}
                                    className={cn(
                                      "border rounded-lg p-4 transition-colors",
                                      token.is_active
                                        ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                                        : "bg-muted/30 border-muted"
                                    )}
                                  >
                                    <div className="flex items-start justify-between mb-3">
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                          <Badge
                                            variant="outline"
                                            className={cn(
                                              "capitalize",
                                              token.platform === "ios" &&
                                                "border-blue-300 text-blue-700 dark:text-blue-300",
                                              token.platform === "android" &&
                                                "border-green-300 text-green-700 dark:text-green-300",
                                              token.platform === "web" &&
                                                "border-purple-300 text-purple-700 dark:text-purple-300"
                                            )}
                                          >
                                            {token.platform}
                                          </Badge>
                                          {token.is_active && (
                                            <Badge className="bg-green-500 hover:bg-green-600 text-white">
                                              Active
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right text-sm text-muted-foreground">
                                        <div>Last Updated</div>
                                        <div className="font-medium">
                                          {formatAuthDate(token.updated_at)}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                      {token.device_model && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            Device:
                                          </span>{" "}
                                          <span className="font-medium">
                                            {token.device_model}
                                          </span>
                                        </div>
                                      )}
                                      {token.app_version && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            App Version:
                                          </span>{" "}
                                          <span className="font-medium">
                                            {token.app_version}
                                          </span>
                                        </div>
                                      )}
                                      {token.os_version && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            OS Version:
                                          </span>{" "}
                                          <span className="font-medium">
                                            {token.os_version}
                                          </span>
                                        </div>
                                      )}
                                      <div>
                                        <span className="text-muted-foreground">
                                          Created:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {formatAuthDate(token.created_at)}
                                        </span>
                                      </div>
                                      {token.last_used_at && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            Last Used:
                                          </span>{" "}
                                          <span className="font-medium">
                                            {formatAuthDate(token.last_used_at)}
                                          </span>
                                        </div>
                                      )}
                                      <div>
                                        <span className="text-muted-foreground">
                                          Token:
                                        </span>{" "}
                                        <span className="font-medium font-mono text-xs">
                                          {token.push_token.length > 20
                                            ? `${token.push_token.substring(
                                                0,
                                                10
                                              )}...${token.push_token.substring(
                                                token.push_token.length - 10
                                              )}`
                                            : token.push_token}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Metadata Cards */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* App Metadata Card */}
                        <Card className="border-l-4 border-l-indigo-500">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Settings className="h-4 w-4" />
                              App Metadata
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {authUserInfo?.app_metadata ? (
                              <div className="bg-muted/30 rounded-lg p-3">
                                <pre className="text-xs overflow-auto max-h-48">
                                  {JSON.stringify(
                                    authUserInfo.app_metadata,
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-muted/30 rounded-lg">
                                <p className="text-muted-foreground">
                                  No app metadata available
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        {/* User Metadata Card */}
                        <Card className="border-l-4 border-l-pink-500">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <User className="h-4 w-4" />
                              User Metadata
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {authUserInfo?.user_metadata ? (
                              <div className="bg-muted/30 rounded-lg p-3">
                                <pre className="text-xs overflow-auto max-h-48">
                                  {JSON.stringify(
                                    authUserInfo.user_metadata,
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-muted/30 rounded-lg">
                                <p className="text-muted-foreground">
                                  No user metadata available
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">
                        Only administrators can view authentication details
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="subscription" className="mt-4">
              <div className="space-y-6">
                {/* Current Subscription Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Current Subscription
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingSubscription || isLoadingLatestSubscription ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : currentSubscription &&
                      getEffectiveStatus(
                        currentSubscription.status,
                        currentSubscription.subscriptionEnd
                      ) === "active" ? (
                      (() => {
                        // Only show the active current subscription
                        const displaySubscription = currentSubscription;
                        if (!displaySubscription) return null;

                        return (
                          <div className="space-y-4">
                            {/* Plan Overview */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div>
                                <h3 className="text-xl font-semibold">
                                  {displaySubscription.subscriptionPlan?.name ||
                                    "Unknown Plan"}
                                </h3>
                                <p className="text-muted-foreground">
                                  {displaySubscription.subscriptionPlan
                                    ?.description || "No description available"}
                                </p>
                              </div>
                              <div className="text-left sm:text-right">
                                <div className="text-2xl font-bold">
                                  ₹{displaySubscription.price.toFixed(2)}
                                </div>
                                {(() => {
                                  const effectiveStatus = getEffectiveStatus(
                                    displaySubscription.status,
                                    displaySubscription.subscriptionEnd
                                  );
                                  return (
                                    <Badge
                                      className={getStatusColor(
                                        effectiveStatus
                                      )}
                                    >
                                      {formatStatusText(effectiveStatus)}
                                    </Badge>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Key Details Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground mb-1">
                                  Billing Cycle
                                </div>
                                <div className="font-medium">
                                  {displaySubscription.subscriptionPlan
                                    ?.durationType === "days"
                                    ? `${displaySubscription.subscriptionPlan.duration} Days`
                                    : displaySubscription.subscriptionPlan
                                        ?.durationType === "months"
                                    ? `${
                                        displaySubscription.subscriptionPlan
                                          .duration
                                      } Month${
                                        (displaySubscription.subscriptionPlan
                                          .duration || 0) > 1
                                          ? "s"
                                          : ""
                                      }`
                                    : displaySubscription.subscriptionPlan
                                        ?.durationType === "years"
                                    ? `${
                                        displaySubscription.subscriptionPlan
                                          .duration
                                      } Year${
                                        (displaySubscription.subscriptionPlan
                                          .duration || 0) > 1
                                          ? "s"
                                          : ""
                                      }`
                                    : "Custom"}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground mb-1">
                                  Start Date
                                </div>
                                <div className="font-medium">
                                  {format(
                                    new Date(
                                      displaySubscription.subscriptionStart ||
                                        new Date()
                                    ),
                                    "MMM dd, yyyy"
                                  )}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground mb-1">
                                  End Date
                                </div>
                                <div className="font-medium">
                                  {displaySubscription.subscriptionEnd
                                    ? format(
                                        new Date(
                                          displaySubscription.subscriptionEnd
                                        ),
                                        "MMM dd, yyyy"
                                      )
                                    : "Lifetime"}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-muted-foreground mb-1">
                                  Auto-Renew
                                </div>
                                <div className="font-medium">
                                  {displaySubscription.autoRenew
                                    ? "Enabled"
                                    : "Disabled"}
                                </div>
                              </div>
                            </div>

                            {/* Additional Details Section */}
                            <div className="space-y-4 pt-4 border-t">
                              {/* Pricing & Discounts */}
                              {(displaySubscription.totalDiscount ||
                                displaySubscription.promoDiscount ||
                                displaySubscription.referralDiscount ||
                                displaySubscription.paymentMethod) && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Pricing Details
                                  </h4>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {displaySubscription.totalDiscount !=
                                      null &&
                                      displaySubscription.totalDiscount > 0 && (
                                        <div className="bg-green-50 dark:bg-green-950/20 rounded-md p-3">
                                          <div className="text-xs text-muted-foreground">
                                            Total Discount
                                          </div>
                                          <div className="font-medium text-green-600 dark:text-green-400">
                                            -₹
                                            {displaySubscription.totalDiscount.toFixed(
                                              2
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    {displaySubscription.promoDiscount !=
                                      null &&
                                      displaySubscription.promoDiscount > 0 && (
                                        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-md p-3">
                                          <div className="text-xs text-muted-foreground">
                                            Promo Discount
                                          </div>
                                          <div className="font-medium text-blue-600 dark:text-blue-400">
                                            -₹
                                            {displaySubscription.promoDiscount.toFixed(
                                              2
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    {displaySubscription.referralDiscount !=
                                      null &&
                                      displaySubscription.referralDiscount >
                                        0 && (
                                        <div className="bg-green-50 dark:bg-green-950/20 rounded-md p-3">
                                          <div className="text-xs text-muted-foreground">
                                            Referral Discount
                                          </div>
                                          <div className="font-medium text-green-600 dark:text-green-400">
                                            -₹
                                            {displaySubscription.referralDiscount.toFixed(
                                              2
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    {displaySubscription.paymentMethod && (
                                      <div className="bg-muted/50 rounded-md p-3">
                                        <div className="text-xs text-muted-foreground">
                                          Payment Method
                                        </div>
                                        <div className="font-medium">
                                          {
                                            displaySubscription.paymentMethod
                                              .name
                                          }
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Trial Information */}
                              {displaySubscription.isTrial && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Trial Information
                                  </h4>
                                  <div className="bg-amber-50 dark:bg-amber-950/20 rounded-md p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge
                                        variant="outline"
                                        className="bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border-amber-300"
                                      >
                                        Trial Subscription
                                      </Badge>
                                    </div>
                                    {displaySubscription.trialEndDate && (
                                      <div className="text-sm">
                                        <span className="text-muted-foreground">
                                          Trial ends:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {format(
                                            new Date(
                                              displaySubscription.trialEndDate
                                            ),
                                            "MMM dd, yyyy"
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Refund Information */}
                              {displaySubscription.refunded && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Refund Information
                                  </h4>
                                  <div className="bg-red-50 dark:bg-red-950/20 rounded-md p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge
                                        variant="outline"
                                        className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border-red-300"
                                      >
                                        Refunded
                                      </Badge>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                      {displaySubscription.refundAmount && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            Amount:
                                          </span>{" "}
                                          <span className="font-medium">
                                            ₹
                                            {displaySubscription.refundAmount.toFixed(
                                              2
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {displaySubscription.refundDate && (
                                        <div>
                                          <span className="text-muted-foreground">
                                            Date:
                                          </span>{" "}
                                          <span className="font-medium">
                                            {format(
                                              new Date(
                                                displaySubscription.refundDate
                                              ),
                                              "MMM dd, yyyy"
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Technical Details */}
                              {(displaySubscription.deviceType ||
                                displaySubscription.osPlatform ||
                                displaySubscription.appVersion ||
                                displaySubscription.conversionSource ||
                                displaySubscription.acquisitionChannel) && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Technical Details
                                  </h4>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                    {displaySubscription.deviceType && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Device:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {displaySubscription.deviceType}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.osPlatform && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Platform:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {displaySubscription.osPlatform}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.appVersion && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          App Version:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {displaySubscription.appVersion}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.conversionSource && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Source:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {displaySubscription.conversionSource}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.acquisitionChannel && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Channel:
                                        </span>{" "}
                                        <span className="font-medium">
                                          {
                                            displaySubscription.acquisitionChannel
                                          }
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.paymentReference && (
                                      <div>
                                        <span className="text-muted-foreground">
                                          Payment Ref:
                                        </span>{" "}
                                        <span className="font-medium font-mono text-xs">
                                          {displaySubscription.paymentReference}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Admin Notes */}
                              {displaySubscription.adminNotes && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Admin Notes
                                  </h4>
                                  <div className="bg-muted/50 rounded-md p-3">
                                    <p className="text-sm whitespace-pre-wrap">
                                      {displaySubscription.adminNotes}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Subscription History Events */}
                              {(displaySubscription.upgradeDate ||
                                displaySubscription.downgradeDate ||
                                displaySubscription.cancellationDate) && (
                                <div>
                                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                    Subscription Events
                                  </h4>
                                  <div className="space-y-2">
                                    {displaySubscription.upgradeDate && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge
                                          variant="outline"
                                          className="bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300"
                                        >
                                          Upgraded
                                        </Badge>
                                        <span className="text-muted-foreground">
                                          {format(
                                            new Date(
                                              displaySubscription.upgradeDate
                                            ),
                                            "MMM dd, yyyy 'at' h:mm a"
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.downgradeDate && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge
                                          variant="outline"
                                          className="bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-300"
                                        >
                                          Downgraded
                                        </Badge>
                                        <span className="text-muted-foreground">
                                          {format(
                                            new Date(
                                              displaySubscription.downgradeDate
                                            ),
                                            "MMM dd, yyyy 'at' h:mm a"
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {displaySubscription.cancellationDate && (
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-2 text-sm">
                                          <Badge
                                            variant="outline"
                                            className="bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300"
                                          >
                                            Cancelled
                                          </Badge>
                                          <span className="text-muted-foreground">
                                            {format(
                                              new Date(
                                                displaySubscription.cancellationDate
                                              ),
                                              "MMM dd, yyyy 'at' h:mm a"
                                            )}
                                          </span>
                                        </div>
                                        {displaySubscription.cancellationReason && (
                                          <div className="ml-6 text-sm text-muted-foreground italic">
                                            Reason:{" "}
                                            {
                                              displaySubscription.cancellationReason
                                            }
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Progress Bar (if subscription has end date) */}
                            {displaySubscription.subscriptionEnd && (
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    Progress
                                  </span>
                                  <span className="text-muted-foreground">
                                    {calculateProgress(
                                      displaySubscription.subscriptionStart,
                                      displaySubscription.subscriptionEnd
                                    )}
                                    % complete
                                  </span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div
                                    className="bg-primary h-2 rounded-full transition-all duration-300"
                                    style={{
                                      width: `${calculateProgress(
                                        displaySubscription.subscriptionStart,
                                        displaySubscription.subscriptionEnd
                                      )}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Management Actions */}
                            {hasAdminPrivileges() &&
                              canManageUserRoles() &&
                              currentSubscription && (
                                <div className="flex flex-wrap gap-2 pt-4 border-t">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleExtendSubscription}
                                  >
                                    Extend Period
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleUpgradeSubscription}
                                  >
                                    Change Plan
                                  </Button>
                                  {currentSubscription.status !== "active" && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleChangeStatus}
                                    >
                                      Change Status
                                    </Button>
                                  )}
                                  {currentSubscription.status === "active" && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-destructive hover:bg-destructive/10 border-destructive/30"
                                      onClick={handleCancelSubscription}
                                    >
                                      Cancel Subscription
                                    </Button>
                                  )}
                                </div>
                              )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-muted-foreground mb-4">
                          No active subscription found for {user.name}
                        </div>
                        {hasAdminPrivileges() && canManageUserRoles() && (
                          <Button onClick={handleChangeSubscription}>
                            <CreditCard className="mr-2 h-4 w-4" />
                            Assign Subscription
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Subscription History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      Subscription History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isLoadingSubscription ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : subscriptionHistory.length === 0 ? (
                      <div className="text-center py-6 bg-muted/30 rounded-lg">
                        <p className="text-muted-foreground">
                          No subscription history found
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {subscriptionHistory
                          .slice(0, 10)
                          .map((subscription) => (
                            <div
                              key={subscription.id}
                              className="border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-medium">
                                    {subscription.subscriptionPlan?.name ||
                                      "Unknown Plan"}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {format(
                                      new Date(subscription.subscriptionStart),
                                      "MMM dd, yyyy"
                                    )}{" "}
                                    -{" "}
                                    {subscription.subscriptionEnd
                                      ? format(
                                          new Date(
                                            subscription.subscriptionEnd
                                          ),
                                          "MMM dd, yyyy"
                                        )
                                      : "Ongoing"}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <div className="font-semibold">
                                    ₹{subscription.price.toFixed(2)}
                                  </div>
                                  {(() => {
                                    const effectiveStatus = getEffectiveStatus(
                                      subscription.status,
                                      subscription.subscriptionEnd
                                    );
                                    return (
                                      <Badge
                                        className={getStatusColor(
                                          effectiveStatus
                                        )}
                                        variant="secondary"
                                      >
                                        {formatStatusText(effectiveStatus)}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">
                                    Duration:
                                  </span>{" "}
                                  <span className="font-medium">
                                    {subscription.subscriptionPlan
                                      ?.durationType === "days"
                                      ? `${subscription.subscriptionPlan.duration} Days`
                                      : subscription.subscriptionPlan
                                          ?.durationType === "months"
                                      ? `${
                                          subscription.subscriptionPlan.duration
                                        } Month${
                                          subscription.subscriptionPlan
                                            .duration > 1
                                            ? "s"
                                            : ""
                                        }`
                                      : subscription.subscriptionPlan
                                          ?.durationType === "years"
                                      ? `${
                                          subscription.subscriptionPlan.duration
                                        } Year${
                                          subscription.subscriptionPlan
                                            .duration > 1
                                            ? "s"
                                            : ""
                                        }`
                                      : "Custom"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">
                                    Payment:
                                  </span>{" "}
                                  <span className="font-medium">
                                    {subscription.paymentMethod?.name ||
                                      "Not specified"}
                                  </span>
                                </div>
                                {subscription.totalDiscount != null &&
                                  subscription.totalDiscount > 0 && (
                                    <div>
                                      <span className="text-muted-foreground">
                                        Total Discount:
                                      </span>{" "}
                                      <span className="font-medium text-green-600">
                                        -₹
                                        {subscription.totalDiscount.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                {subscription.promoDiscount != null &&
                                  subscription.promoDiscount > 0 && (
                                    <div>
                                      <span className="text-muted-foreground">
                                        Promo Discount:
                                      </span>{" "}
                                      <span className="font-medium text-blue-600">
                                        -₹
                                        {subscription.promoDiscount.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                {subscription.referralDiscount != null &&
                                  subscription.referralDiscount > 0 && (
                                    <div>
                                      <span className="text-muted-foreground">
                                        Referral Discount:
                                      </span>{" "}
                                      <span className="font-medium text-green-600">
                                        -₹
                                        {subscription.referralDiscount.toFixed(
                                          2
                                        )}
                                      </span>
                                    </div>
                                  )}
                                {subscription.isTrial && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Type:
                                    </span>{" "}
                                    <Badge
                                      variant="outline"
                                      className="bg-amber-100 text-amber-700 border-amber-300 text-xs"
                                    >
                                      Trial
                                    </Badge>
                                  </div>
                                )}
                                {subscription.refunded && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Refunded:
                                    </span>{" "}
                                    <span className="font-medium text-red-600">
                                      ₹
                                      {subscription.refundAmount?.toFixed(2) ||
                                        "0.00"}
                                    </span>
                                  </div>
                                )}
                                {subscription.deviceType && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Device:
                                    </span>{" "}
                                    <span className="font-medium">
                                      {subscription.deviceType}
                                    </span>
                                  </div>
                                )}
                                {subscription.conversionSource && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Source:
                                    </span>{" "}
                                    <span className="font-medium">
                                      {subscription.conversionSource}
                                    </span>
                                  </div>
                                )}
                                {subscription.cancellationDate && (
                                  <div>
                                    <span className="text-muted-foreground">
                                      Cancelled:
                                    </span>{" "}
                                    <span className="font-medium">
                                      {format(
                                        new Date(subscription.cancellationDate),
                                        "MMM dd, yyyy"
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Additional Information */}
                              {(subscription.cancellationReason ||
                                subscription.adminNotes) && (
                                <div className="mt-3 space-y-2">
                                  {subscription.cancellationReason && (
                                    <div className="text-sm">
                                      <span className="text-muted-foreground">
                                        Cancellation Reason:
                                      </span>{" "}
                                      <span className="italic">
                                        {subscription.cancellationReason}
                                      </span>
                                    </div>
                                  )}
                                  {subscription.adminNotes && (
                                    <div className="text-sm">
                                      <span className="text-muted-foreground">
                                        Admin Notes:
                                      </span>{" "}
                                      <span className="italic">
                                        {subscription.adminNotes}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="referrals" className="mt-4">
              <ReferralInfo
                userId={user.id}
                userName={user.name}
                hasAdminPrivileges={hasAdminPrivileges()}
                canManageUser={canManageUserRoles()}
              />
            </TabsContent>

            <TabsContent value="recommendations" className="mt-4">
              <UserRecommendations userId={user.id} userName={user.name} />
            </TabsContent>

            <TabsContent value="notifications" className="mt-4">
              <UserNotifications userId={user.id} userName={user.name} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <CollaboratorSummary
            user={user}
            collaboratorData={collaboratorData}
            collaboratorStats={collaboratorStats}
            isLoadingCollaboratorData={isLoadingCollaboratorData}
          />

          <UserStatistics
            user={user}
            userStats={userStats}
            userCategoryData={userCategoryData}
            hasAdminPrivileges={hasAdminPrivileges()}
            canManageUserRoles={canManageUserRoles()}
            isAddingCategories={isAddingCategories}
            isLoading={isLoadingStats}
            onOpenCategoryPicker={handleOpenCategoryPicker}
            onRemoveCategory={handleRemoveCategory}
          />

          <UserTopicSubscriptions userId={user.id} />

          <UserActions
            user={user}
            authUserInfo={authUserInfo}
            currentSubscription={currentSubscription}
            hasAdminPrivileges={hasAdminPrivileges()}
            canManageUserRoles={canManageUserRoles()}
            isDeleted={!user.isActive}
            loadingStates={{
              isResendingConfirmation,
              isResendingInvitation,
              isBanning,
              isUnbanning,
              isConfirmingEmail,
              isMakingBetaTester,
            }}
            onEditProfile={handleEditProfile}
            onChangeSubscription={handleChangeSubscription}
            onManageRoles={handleOpenManageRoles}
            onResetPassword={handleResetPassword}
            onResendOTP={handleResendOTPDialog}
            onResendInvitation={handleResendInvitationDialog}
            onConfirmEmail={handleConfirmEmailDialog}
            onBanAccount={handleBanAccount}
            onUnbanAccount={handleUnbanAccount}
            onDeleteAccount={handleDeleteAccount}
            onMakeBetaTester={handleMakeBetaTester}
            onPermanentlyDelete={() => setIsPurgeDialogOpen(true)}
            isBetaTester={isBetaTester}
          />
        </div>
      </div>

      {/* Password Reset Confirmation Dialog */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Password Reset
            </DialogTitle>
            <DialogDescription>
              This will send a password reset email to{" "}
              <strong>{user?.email}</strong>. The user will need to click the
              link in the email to set a new password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsResetDialogOpen(false)}
              disabled={isResetting}
            >
              Cancel
            </Button>
            <Button onClick={handleSendPasswordReset} disabled={isResetting}>
              {isResetting ? (
                <>
                  <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-primary border-t-transparent"></span>
                  Sending...
                </>
              ) : (
                "Send Reset Email"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Roles Dialog */}
      <Dialog open={isManagingRoles} onOpenChange={setIsManagingRoles}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Manage Roles for {user?.name}
            </DialogTitle>
            <DialogDescription>
              Assign or remove roles for this user. Each user must have the
              Standard User role, and you can assign one additional role for
              elevated permissions.
            </DialogDescription>
          </DialogHeader>

          {user && (
            <div className="py-4">
              <div className="space-y-4">
                <div className="text-sm font-medium">Current Roles:</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {user.roles && user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Badge
                        key={role.id}
                        variant="outline"
                        className={cn("border", getRoleBadgeColor(role.id))}
                      >
                        {role.isSystem && <Shield className="h-3 w-3 mr-1" />}
                        {role.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      No roles assigned
                    </span>
                  )}
                </div>

                {/* Display warning message if needed */}
                {unauthorizedMessage && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4">
                    <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-400">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">{unauthorizedMessage}</span>
                    </div>
                  </div>
                )}

                <div className="text-sm font-medium border-t pt-4">
                  User Roles
                </div>
                <div className="space-y-3 border rounded-md p-3">
                  {/* Standard User Role - Always shown first and readonly */}
                  {userRoles
                    .filter((role) => role.id === 5)
                    .map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center space-x-3 rounded-md border p-2 bg-muted/30 border-muted"
                      >
                        <Checkbox
                          id={`role-detail-${role.id}`}
                          checked={true}
                          disabled={true}
                        />
                        <Label
                          htmlFor={`role-detail-${role.id}`}
                          className="cursor-not-allowed flex-1"
                        >
                          <div className="flex items-center justify-between">
                            <span>{role.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              Required
                            </Badge>
                          </div>
                        </Label>
                      </div>
                    ))}

                  {/* Additional Role Selection */}
                  <div className="border-t pt-3 mt-3">
                    <Label className="text-sm font-medium text-muted-foreground mb-2 block">
                      Additional Role (Optional - Select one)
                    </Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userRoles
                        .filter((role) => role.id !== 5)
                        .map((role) => {
                          const isDisabled = !canManageRole(role.id);
                          const isSelected = selectedRoleIds.includes(role.id);

                          return (
                            <div
                              key={role.id}
                              className={cn(
                                "flex items-center space-x-3 rounded-md border p-2 transition-colors",
                                isSelected && "bg-primary/5 border-primary/20",
                                isDisabled && "opacity-50"
                              )}
                            >
                              <Checkbox
                                id={`role-detail-${role.id}`}
                                checked={isSelected}
                                onCheckedChange={() => {
                                  // Only allow one additional role besides Standard User (ID 5)
                                  if (isSelected) {
                                    setSelectedRoleIds((prev) =>
                                      prev.filter((id) => id !== role.id)
                                    );
                                  } else {
                                    setSelectedRoleIds([5, role.id]);
                                  }
                                }}
                                disabled={isDisabled}
                              />
                              <Label
                                htmlFor={`role-detail-${role.id}`}
                                className={cn(
                                  "flex-1",
                                  isDisabled
                                    ? "cursor-not-allowed"
                                    : "cursor-pointer"
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-medium">
                                      {role.name}
                                    </div>
                                    {role.description && (
                                      <div className="text-xs text-muted-foreground">
                                        {role.description}
                                      </div>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <Badge className="text-xs">Selected</Badge>
                                  )}
                                </div>
                              </Label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsManagingRoles(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRoles}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Reset form when dialog is closed
            setDeleteReason("");
            setDeleteAdditionalFeedback("");
          }
          setIsDeleteDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm Account Deletion
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the account for{" "}
              <strong>{user?.name}</strong> with email{" "}
              <strong>{user?.email}</strong>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-reason">
                Reason for deletion <span className="text-destructive">*</span>
              </Label>
              <Input
                id="delete-reason"
                placeholder="e.g., User requested account deletion, Policy violation, etc."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                disabled={isDeleting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="delete-feedback">
                Additional feedback (optional)
              </Label>
              <Textarea
                id="delete-feedback"
                placeholder="Any additional context or notes about this deletion..."
                value={deleteAdditionalFeedback}
                onChange={(e) => setDeleteAdditionalFeedback(e.target.value)}
                disabled={isDeleting}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAccount}
              disabled={isDeleting || !deleteReason.trim()}
            >
              {isDeleting ? (
                <>
                  <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-background border-t-transparent"></span>
                  Deleting...
                </>
              ) : (
                "Delete Account"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban/Unban Confirmation Dialog */}
      <Dialog open={isBanDialogOpen} onOpenChange={setIsBanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm {user?.isActive ? "Ban" : "Unban"} User
            </DialogTitle>
            <DialogDescription>
              {user?.isActive
                ? `This will ban ${user?.name} and prevent them from accessing the system. They will not be able to log in until unbanned.`
                : `This will unban ${user?.name} and restore their access to the system.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsBanDialogOpen(false)}
              disabled={user?.isActive ? isBanning : isUnbanning}
            >
              Cancel
            </Button>
            <Button
              variant={user?.isActive ? "destructive" : "default"}
              onClick={confirmBanUnbanAction}
              disabled={user?.isActive ? isBanning : isUnbanning}
            >
              {user?.isActive
                ? isBanning
                  ? "Banning..."
                  : "Ban User"
                : isUnbanning
                ? "Unbanning..."
                : "Unban User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend OTP Confirmation Dialog */}
      <Dialog open={isOtpDialogOpen} onOpenChange={setIsOtpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-primary" />
              Confirm Resend OTP
            </DialogTitle>
            <DialogDescription>
              This will send a new verification OTP to{" "}
              <strong>{user?.email}</strong>. The user will need to use this OTP
              to verify their email address.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsOtpDialogOpen(false)}
              disabled={isResendingConfirmation}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendOTP}
              disabled={isResendingConfirmation}
            >
              {isResendingConfirmation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send OTP"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Invitation Confirmation Dialog */}
      <Dialog
        open={isInvitationDialogOpen}
        onOpenChange={setIsInvitationDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Confirm Resend Invitation
            </DialogTitle>
            <DialogDescription>
              This will send a new invitation email to{" "}
              <strong>{user?.email}</strong>. The user will need to click the
              link in the email to set up their account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsInvitationDialogOpen(false)}
              disabled={isResendingInvitation}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResendInvitation}
              disabled={isResendingInvitation}
            >
              {isResendingInvitation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manually Confirm Email Confirmation Dialog */}
      <Dialog
        open={isConfirmEmailDialogOpen}
        onOpenChange={setIsConfirmEmailDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-primary" />
              Confirm Manual Email Verification
            </DialogTitle>
            <DialogDescription>
              This will manually mark <strong>{user?.email}</strong> as verified
              without requiring the user to complete the verification process.
              Use this only when necessary.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsConfirmEmailDialogOpen(false)}
              disabled={isConfirmingEmail}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmEmail} disabled={isConfirmingEmail}>
              {isConfirmingEmail ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Confirming...
                </>
              ) : (
                "Confirm Email"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanently Purge User Data Dialog */}
      <Dialog
        open={isPurgeDialogOpen}
        onOpenChange={(open) => {
          if (!open) setIsPurgeDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently Delete All Data
            </DialogTitle>
            <DialogDescription>
              This action will permanently delete all records related to
              <strong> {user?.name} </strong> from the system, including their
              profile and any related records. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsPurgeDialogOpen(false)}
              disabled={isPurging}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurgeUserData}
              disabled={isPurging}
            >
              {isPurging ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete All Data Permanently"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Management Dialog */}
      <Dialog
        open={isSubscriptionDialogOpen}
        onOpenChange={setIsSubscriptionDialogOpen}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {currentSubscription
                ? `Update Subscription for ${user?.name}`
                : `Assign Subscription to ${user?.name}`}
            </DialogTitle>
            <DialogDescription>
              {currentSubscription
                ? "Update the user's subscription plan, pricing, or billing details."
                : "Assign a subscription plan to this user with custom access levels and billing options."}
            </DialogDescription>
          </DialogHeader>

          {isLoadingSubscription ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <UserSubscriptionForm
              userId={user.id}
              userName={user.name}
              currentSubscription={currentSubscription}
              subscriptionPlans={subscriptionPlans}
              currencies={currencies}
              paymentMethods={paymentMethods}
              isProcessing={createSubscriptionMutation.isPending}
              onSubmit={handleSubscriptionSubmit}
              onCancel={() => setIsSubscriptionDialogOpen(false)}
              promotions={promotions}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add these components at the end of the file, before the closing AdminLayout tag */}

      {/* Subscription Management Dialogs */}
      {currentSubscription && (
        <>
          <CancelSubscriptionDialog
            open={isCancelDialogOpen}
            onOpenChange={setIsCancelDialogOpen}
            onConfirm={cancelSubscriptionMutation.mutate}
            subscription={currentSubscription}
            isProcessing={cancelSubscriptionMutation.isPending}
            userName={user.name}
          />
          <ExtendSubscriptionDialog
            open={isExtendDialogOpen}
            onOpenChange={setIsExtendDialogOpen}
            onConfirm={extendSubscriptionMutation.mutate}
            subscription={currentSubscription}
            isProcessing={extendSubscriptionMutation.isPending}
            userName={user.name}
          />
          <ChangeSubscriptionStatusDialog
            open={isStatusDialogOpen}
            onOpenChange={setIsStatusDialogOpen}
            onConfirm={changeStatusMutation.mutate}
            subscription={currentSubscription}
            isProcessing={changeStatusMutation.isPending}
            userName={user.name}
          />
          <UpgradeSubscriptionDialog
            open={isUpgradeDialogOpen}
            onOpenChange={setIsUpgradeDialogOpen}
            onConfirm={upgradeSubscriptionMutation.mutate}
            subscription={currentSubscription}
            availablePlans={subscriptionPlans}
            isProcessing={upgradeSubscriptionMutation.isPending}
            userName={user.name}
          />
        </>
      )}

      {/* Category Picker Dialog */}
      <Dialog open={showCategoryPicker} onOpenChange={setShowCategoryPicker}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Categories for {user?.name}</DialogTitle>
            <DialogDescription>
              Select categories to add to this user's profile. Categories
              already assigned to this user are not shown.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {/* Show currently assigned categories if any */}
            {userCategoryData.length > 0 && (
              <div className="mb-4 p-3 bg-muted/30 rounded-md">
                <p className="text-sm font-medium mb-2">
                  Currently assigned categories:
                </p>
                <div className="flex flex-wrap gap-1">
                  {userCategoryData.map((category) => (
                    <Badge
                      key={category.id}
                      variant="secondary"
                      className="text-xs"
                    >
                      {category.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {availableCategories
                .filter(
                  (category) =>
                    !userCategoryData.some(
                      (userCat) => userCat.id === category.id
                    )
                )
                .map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`category-${category.id}`}
                      checked={selectedCategoryIds.includes(category.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedCategoryIds((prev) => [
                            ...prev,
                            category.id,
                          ]);
                        } else {
                          setSelectedCategoryIds((prev) =>
                            prev.filter((id) => id !== category.id)
                          );
                        }
                      }}
                    />
                    <label
                      htmlFor={`category-${category.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {category.name}
                    </label>
                  </div>
                ))}
              {availableCategories.filter(
                (category) =>
                  !userCategoryData.some(
                    (userCat) => userCat.id === category.id
                  )
              ).length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  All available categories have already been assigned to this
                  user.
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCategoryPicker(false)}
              disabled={isAddingCategories}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedCategories}
              disabled={isAddingCategories || selectedCategoryIds.length === 0}
            >
              {isAddingCategories ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                `Add ${selectedCategoryIds.length} Categories`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UserDetails;
